
import AsyncStorage from '@react-native-async-storage/async-storage';
import { supabase } from '../lib/supabase';
import { safeLog } from '../utils/safeLog';

export interface PersistentContext {
  userId: string;
  keyTopics: string[];
  emotionalPatterns: Array<{
    pattern: string;
    frequency: number;
    lastSeen: string;
  }>;
  importantEvents: Array<{
    event: string;
    date: string;
    significance: 'high' | 'medium' | 'low';
  }>;
  preferredSupport: {
    style: string;
    effectiveStrategies: string[];
  };
  relationshipDynamics: {
    trustLevel: number;
    communicationStyle: string;
    boundaries: string[];
  };
  longTermGoals: string[];
  triggerWarnings: string[];
  createdAt: string;
  updatedAt: string;
}

class ContextPersistenceService {
  private static instance: ContextPersistenceService;
  private readonly CONTEXT_STORAGE_KEY = 'bill_persistent_context';
  private readonly CONTEXT_UPDATE_INTERVAL = 5 * 60 * 1000; // 5 minutes

  static getInstance(): ContextPersistenceService {
    if (!ContextPersistenceService.instance) {
      ContextPersistenceService.instance = new ContextPersistenceService();
    }
    return ContextPersistenceService.instance;
  }

  async savePersistentContext(context: PersistentContext): Promise<void> {
    try {
      // Save to AsyncStorage for quick access
      await AsyncStorage.setItem(
        `${this.CONTEXT_STORAGE_KEY}_${context.userId}`,
        JSON.stringify(context)
      );

      // Save to database for long-term persistence
      const { error } = await supabase
        .from('bill_persistent_context')
        .upsert({
          user_id: context.userId,
          key_topics: context.keyTopics,
          emotional_patterns: context.emotionalPatterns,
          important_events: context.importantEvents,
          preferred_support: context.preferredSupport,
          relationship_dynamics: context.relationshipDynamics,
          long_term_goals: context.longTermGoals,
          trigger_warnings: context.triggerWarnings,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        });

      if (error) {
        safeLog.error('❌ ContextPersistence: Error saving to database:', error);
      } else {
        safeLog.debug('✅ ContextPersistence: Context saved successfully');
      }
    } catch (error) {
      safeLog.error('❌ ContextPersistence: Error saving context:', error);
    }
  }

  async loadPersistentContext(userId: string): Promise<PersistentContext | null> {
    try {
      // Try AsyncStorage first
      const stored = await AsyncStorage.getItem(`${this.CONTEXT_STORAGE_KEY}_${userId}`);
      if (stored) {
        const context = JSON.parse(stored);
        safeLog.debug('✅ ContextPersistence: Loaded from AsyncStorage');
        return context;
      }

      // Fallback to database
      const { data, error } = await supabase
        .from('bill_persistent_context')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error || !data) {
        safeLog.debug('📱 ContextPersistence: No existing context found');
        return null;
      }

      const context: PersistentContext = {
        userId: data.user_id,
        keyTopics: data.key_topics || [],
        emotionalPatterns: data.emotional_patterns || [],
        importantEvents: data.important_events || [],
        preferredSupport: data.preferred_support || { style: '', effectiveStrategies: [] },
        relationshipDynamics: data.relationship_dynamics || { trustLevel: 0, communicationStyle: '', boundaries: [] },
        longTermGoals: data.long_term_goals || [],
        triggerWarnings: data.trigger_warnings || [],
        createdAt: data.created_at,
        updatedAt: data.updated_at
      };

      // Cache in AsyncStorage
      await AsyncStorage.setItem(
        `${this.CONTEXT_STORAGE_KEY}_${userId}`,
        JSON.stringify(context)
      );

      safeLog.debug('✅ ContextPersistence: Loaded from database');
      return context;
    } catch (error) {
      safeLog.error('❌ ContextPersistence: Error loading context:', error);
      return null;
    }
  }

  async updateContextFromConversation(
    userId: string,
    messages: Array<{ role: 'user' | 'assistant'; content: string; timestamp: Date }>
  ): Promise<void> {
    try {
      let context = await this.loadPersistentContext(userId);
      
      if (!context) {
        context = this.createEmptyContext(userId);
      }

      // Analyze messages for context updates
      const userMessages = messages.filter(m => m.role === 'user');
      const recentUserText = userMessages.map(m => m.content).join(' ').toLowerCase();

      // Extract key topics using simple keyword matching
      const topicKeywords = {
        'sobriety_milestones': ['dias sóbrio', 'sobriedade', 'milestone', 'marco'],
        'anxiety': ['ansioso', 'preocupado', 'nervoso', 'ansiedade'],
        'depression': ['triste', 'deprimido', 'melancolia', 'depressão'],
        'cravings': ['vontade de beber', 'tentação', 'crave', 'desejo'],
        'family': ['família', 'esposa', 'marido', 'filhos', 'pais'],
        'work': ['trabalho', 'emprego', 'chefe', 'carreira'],
        'relationships': ['relacionamento', 'amigos', 'namorado', 'namorada'],
        'meetings': ['reunião', 'grupo', 'aa', 'encontro']
      };

      // Update key topics
      Object.entries(topicKeywords).forEach(([topic, keywords]) => {
        if (keywords.some(keyword => recentUserText.includes(keyword))) {
          if (!context.keyTopics.includes(topic)) {
            context.keyTopics.push(topic);
          }
        }
      });

      // Update emotional patterns
      const emotionKeywords = {
        'anxiety_pattern': ['ansioso', 'preocupado', 'nervoso'],
        'sadness_pattern': ['triste', 'deprimido', 'melancolia'],
        'optimism_pattern': ['bem', 'feliz', 'animado', 'otimista'],
        'frustration_pattern': ['frustrado', 'irritado', 'chateado']
      };

      Object.entries(emotionKeywords).forEach(([pattern, keywords]) => {
        if (keywords.some(keyword => recentUserText.includes(keyword))) {
          const existingPattern = context.emotionalPatterns.find(p => p.pattern === pattern);
          if (existingPattern) {
            existingPattern.frequency += 1;
            existingPattern.lastSeen = new Date().toISOString();
          } else {
            context.emotionalPatterns.push({
              pattern,
              frequency: 1,
              lastSeen: new Date().toISOString()
            });
          }
        }
      });

      // Detect important events
      const importantIndicators = ['parabéns', 'conquista', 'vitória', 'recaída', 'problema sério', 'crise'];
      if (importantIndicators.some(indicator => recentUserText.includes(indicator))) {
        const eventSummary = userMessages[userMessages.length - 1]?.content.substring(0, 100) || 'Evento importante';
        context.importantEvents.push({
          event: eventSummary,
          date: new Date().toISOString(),
          significance: 'high'
        });

        // Keep only last 20 events
        if (context.importantEvents.length > 20) {
          context.importantEvents = context.importantEvents.slice(-20);
        }
      }

      context.updatedAt = new Date().toISOString();
      await this.savePersistentContext(context);
      
      safeLog.debug('✅ ContextPersistence: Context updated from conversation', {
        userId,
        keyTopicsCount: context.keyTopics.length,
        emotionalPatternsCount: context.emotionalPatterns.length,
        importantEventsCount: context.importantEvents.length
      });
    } catch (error) {
      safeLog.error('❌ ContextPersistence: Error updating context:', error);
    }
  }

  private createEmptyContext(userId: string): PersistentContext {
    return {
      userId,
      keyTopics: [],
      emotionalPatterns: [],
      importantEvents: [],
      preferredSupport: {
        style: 'compassionate',
        effectiveStrategies: []
      },
      relationshipDynamics: {
        trustLevel: 1,
        communicationStyle: 'supportive',
        boundaries: []
      },
      longTermGoals: [],
      triggerWarnings: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
  }

  async clearContext(userId: string): Promise<void> {
    try {
      await AsyncStorage.removeItem(`${this.CONTEXT_STORAGE_KEY}_${userId}`);
      
      const { error } = await supabase
        .from('bill_persistent_context')
        .delete()
        .eq('user_id', userId);

      if (error) {
        safeLog.error('❌ ContextPersistence: Error clearing database context:', error);
      }
      
      safeLog.debug('✅ ContextPersistence: Context cleared');
    } catch (error) {
      safeLog.error('❌ ContextPersistence: Error clearing context:', error);
    }
  }
}

export default ContextPersistenceService.getInstance();
